/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { ConfirmationOptions } from '@opengeoweb/shared';
import { useConfirmationDialog } from '@opengeoweb/shared';
import { uiActions } from '@opengeoweb/store';
import {
  useMutateDeleteWarningEvent,
  useMutateEventEditor,
  useWarningEventsList,
} from '../../../api/warning-api/hooks';
import { warningsEventFormActions } from '../../../store/warningsEventForm/reducer';
import type { FormAction } from '../../../store/warningsEventForm/reducer';
import {
  getSelectedEventId,
  getWarningsEventIsFormDirty,
} from '../../../store/warningsEventForm/selectors';
import { PublicWarningStatus } from '../../../store/warningsForm/types';
import type {
  EventWarningForWarningList,
  WarningEvent,
  WarningEventBody,
} from '../../../store/warningsForm/types';
import { warningEventDialogType } from '../../../store/warningsForm/utils';
import { useWarningsTranslation } from '../../../utils/i18n';
import { getWarningsFeatures } from '../../../utils/warningsFeatures';
import type { TimeFilter } from '../../FilterList/filter-utils';
import type { ConfirmDialogProps } from '../../PublicWarningList/ConfirmWarningDialog';

export const getNewEventWithLinkedToId = (
  event: WarningEvent,
): WarningEvent => {
  const { eventId, ...rest } = event;

  const eventWithCleanWarnings = {
    ...rest,
    warnings: rest.warnings.map(
      (warning: WarningEventBody): WarningEventBody => {
        const { eventId, id, ...cleanWarning } = warning;
        return { ...cleanWarning, linked_to_id: id };
      },
    ),
  };

  return {
    ...eventWithCleanWarnings,
    linked_to_id: eventId,
    status: PublicWarningStatus.DRAFT_PUBLISHED,
  };
};

export const warningFormConfirmationOptions = (
  t: TFunction,
): ConfirmationOptions => {
  return {
    cancelLabel: t('close-dialog-cancel'),
    title: t('close-warning-dialog-title'),
    description: t('close-dialog-description'),
    confirmLabel: t('close-dialog-confirm'),
    catchOnCancel: true,
  };
};

interface WarningEventsActions {
  selectedEventId: string;
  openWarningsEventDialog: (
    formAction?: FormAction,
    warning?: EventWarningForWarningList,
  ) => Promise<void>;
  deleteWarningsEvent: (
    warningEventId: string,
    currentUser: string,
    setConfirmDialogOptions: React.Dispatch<
      React.SetStateAction<ConfirmDialogProps | undefined>
    >,
  ) => Promise<void>;
  updateWarningsEvent: (
    warningEventId: string,
    currentUser: string,
  ) => Promise<void>;
}

export const useWarningEventActions = (
  panelId: string,
  timeFilter?: TimeFilter,
): WarningEventsActions => {
  const { t } = useWarningsTranslation();
  const dispatch = useDispatch();
  const confirmDialog = useConfirmationDialog();
  const selectedEventId = useSelector(getSelectedEventId);
  const isEventFormDirty = useSelector(getWarningsEventIsFormDirty);

  const { hasWarningEvents } = getWarningsFeatures();
  const { data: warningEvents, refetch: refetchList } = useWarningEventsList(
    !hasWarningEvents,
    timeFilter,
  );

  const openDialog = async (
    event: WarningEvent | undefined,
    formAction?: FormAction,
    selectedEventId?: string,
  ): Promise<void> => {
    if (isEventFormDirty) {
      const mayProceed = await confirmDialog(warningFormConfirmationOptions(t))
        .then(() => true)
        .catch(() => false);

      if (!mayProceed) {
        return;
      }
    }

    dispatch(
      warningsEventFormActions.openWarningsEventFormDialog({
        ...(event && {
          event,
        }),
        formState: formAction,
        panelId,
        ...(selectedEventId !== undefined ? { selectedEventId } : {}),
      }),
    );
  };

  const openWarningsEventDialog = async (
    formAction?: FormAction,
    warning?: EventWarningForWarningList,
  ): Promise<void> => {
    // If we've already got this event opened in the dialog, do nothing
    if (selectedEventId === warning?.eventId) {
      return;
    }

    // Find corresponding event for warning
    const event = (warningEvents || []).find((event) => {
      return event.eventId === warning?.eventId;
    });

    void openDialog(event, formAction);
  };

  const { mutateAsync: deleteWarningEvent } = useMutateDeleteWarningEvent();

  const deleteWarningsEvent = async (
    warningEventId: string,
    currentUser: string,
    setConfirmDialogOptions: React.Dispatch<
      React.SetStateAction<ConfirmDialogProps | undefined>
    >,
  ): Promise<void> => {
    // Find corresponding event for warning
    const event = (warningEvents || []).find((event) => {
      return event.eventId === warningEventId;
    });

    if (!event) {
      return;
    }

    const editor = event?.editor ?? '';
    const isLocked = editor !== '' && editor !== currentUser;

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-delete-event-confirm'),
      title: t('warning-dialog-delete-event-title'),
      description: t('warning-dialog-delete-event-description'),
      request: async () =>
        void deleteWarningEvent(
          { eventId: event.eventId! },
          {
            onSuccess: () => {
              setConfirmDialogOptions(undefined);
              // Close event dialog if needed
              if (selectedEventId === event.eventId) {
                dispatch(
                  uiActions.setToggleOpenDialog({
                    setOpen: false,
                    type: warningEventDialogType,
                  }),
                );
              }
            },
          },
        ),
      isLocked,
    });
  };

  const updateEventEditor = useMutateEventEditor();

  const updateWarningsEvent = async (
    warningEventId: string,
    currentUser: string,
  ): Promise<void> => {
    // Find corresponding event for warning
    const event = (warningEvents || []).find((event) => {
      return event.eventId === warningEventId;
    });

    if (!event) {
      return;
    }

    const newEvent =
      event.status === PublicWarningStatus.PUBLISHED
        ? getNewEventWithLinkedToId(event)
        : event;

    if (event.eventId && currentUser && !newEvent.editor) {
      void refetchList();
      updateEventEditor.mutate({
        eventId: event.eventId,
        isEditor: true,
      });

      await openDialog(
        {
          ...newEvent,
          editor: currentUser,
        },
        '',
        event.eventId,
      );
    } else {
      void openDialog(newEvent, '', event.eventId);
    }
  };
  return {
    selectedEventId,
    openWarningsEventDialog,
    deleteWarningsEvent,
    updateWarningsEvent,
  };
};
